import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { useLanguage } from "@/i18n/LanguageContext";
import { fetchCfpTopics } from "./api";
import { formatDate, pickLocalized } from "./format";
import { useConferenceSettings } from "./useConferenceSettings";

type TopicCategory = { category: string; topics: string[] };

export function CfpPage() {
  const { t, locale } = useLanguage();
  const { data: conf } = useConferenceSettings();
  const [categories, setCategories] = useState<TopicCategory[]>([]);

  useEffect(() => {
    fetchCfpTopics()
      .then((d) => {
        const raw = (
          locale === "ar" ? d.ar : locale === "fr" ? d.fr ?? d.en : d.en
        ) as unknown;
        if (Array.isArray(raw) && raw.length && typeof raw[0] === "object") {
          setCategories(raw as TopicCategory[]);
        } else if (Array.isArray(raw)) {
          setCategories([{ category: t.sections.topics, topics: raw as string[] }]);
        } else {
          setCategories([]);
        }
      })
      .catch(() => setCategories([]));
  }, [locale, t.sections.topics]);

  return (
    <div>
      <h1 style={{ fontFamily: "var(--dn-font-display)", color: "var(--dn-green-900)" }}>
        {t.sections.cfp}
      </h1>
      <p className="lead">{t.sections.cfpBody}</p>

      {conf && (
        <div className="dn-grid" style={{ marginBottom: "1.5rem" }}>
          <div className="dn-card">
            <h3>{t.sections.theme}</h3>
            <p>{pickLocalized(conf, locale, "theme_en", "theme_ar")}</p>
          </div>
          <div className="dn-card">
            <h3>{t.sections.abstractDeadline}</h3>
            <p>{formatDate(conf.abstract_deadline, locale)}</p>
          </div>
          <div className="dn-card">
            <h3>{t.sections.paperDeadline}</h3>
            <p>{formatDate(conf.paper_deadline, locale)}</p>
          </div>
        </div>
      )}

      <section className="dn-section">
        <h2>{t.sections.guidelines}</h2>
        <ul>
          <li>{t.guidelines.languages}</li>
          <li>{t.guidelines.arabicFont}</li>
          <li>{t.guidelines.englishFont}</li>
          <li>{t.guidelines.frenchFont}</li>
          <li>{t.guidelines.format}</li>
        </ul>
      </section>

      <section className="dn-section">
        <h2>{t.sections.topics}</h2>
        <div className="dn-accordion">
          {categories.map((cat) => (
            <details key={cat.category} open>
              <summary>{cat.category}</summary>
              <ul style={{ marginTop: "0.75rem" }}>
                {cat.topics.map((topic) => (
                  <li key={topic}>{topic}</li>
                ))}
              </ul>
            </details>
          ))}
        </div>
      </section>

      <div className="dn-cta-row">
        <Link className="dn-btn dn-btn-primary" to="/register">
          {t.hero.ctaRegister}
        </Link>
        <Link className="dn-btn dn-btn-gold" to="/submit-paper">
          {t.hero.ctaSubmit}
        </Link>
      </div>
    </div>
  );
}
